import { lazy, Suspense } from 'react';
import { Route, Navigate, useParams } from 'react-router-dom';
import ContentCalendarPage from './pages/ContentCalendarPage';
import ContentReaderPage from './pages/ContentReaderPage';
import AuthorsPage from './pages/AuthorsPage';
import AuthorDetailPage from './pages/AuthorDetailPage';
import DistributionPage from './pages/DistributionPage';
import AnalyticsPage from './pages/AnalyticsPage';
import BrandVoicePage from './pages/BrandVoicePage';
import ContentTemplatesPage from './pages/ContentTemplatesPage';
import ContentTypeConfigsPage from './pages/ContentTypeConfigsPage';
import MediaBrowserPage from './pages/MediaBrowserPage';
import CmsSettingsPage from './pages/CmsSettingsPage';
import { PaneLoader } from '../../shared/components/PaneLoader';

// CRM pages — lazy loaded for pane-crm code-split chunk
const CrmInboxPage = lazy(() => import('../crm/pages/InboxPage'));
const CrmContactsPage = lazy(() => import('../crm/pages/ContactsPage'));
const CrmCompaniesPage = lazy(() => import('../crm/pages/CompaniesPage'));
const CrmDealsPage = lazy(() => import('../crm/pages/DealsPage'));
const CrmPipelineLayoutPage  = lazy(() => import('../crm/pages/PipelineLayoutPage'));
const CrmForecastPage        = lazy(() => import('../crm/pages/ForecastPage'));
const CrmPipelineChangesPage = lazy(() => import('../crm/pages/PipelineChangesPage'));
const CrmRevenuePage         = lazy(() => import('../crm/pages/RevenuePage'));
const CrmAccountsPage        = lazy(() => import('../crm/pages/AccountsPage'));
const CrmTasksPage = lazy(() => import('../crm/pages/TasksPage'));
const CrmFormsPage = lazy(() => import('../crm/pages/FormsPage'));
const MeetingConversationPage = lazy(() => import('../crm/pages/MeetingConversationPage'));
const MeetingsListPage = lazy(() => import('../crm/pages/MeetingsListPage'));
const MeetingDetailPage = lazy(() => import('../crm/pages/MeetingDetailPage'));
const CalendarPage = lazy(() => import('../crm/pages/calendar/CalendarPage'));

const CrmSuspense = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PaneLoader message="Loading CRM…" />}>{children}</Suspense>
);

/**
 * GTM route children — returns <Route> elements as a fragment.
 *
 * These are composed into the parent <Routes> in App.tsx as children
 * of the GTM layout route. NO nested <Routes> — the parent router
 * has full visibility for correct route ranking.
 */
function LegacyContentRedirect() {
  const { itemId } = useParams<{ itemId: string }>();
  return <Navigate to={`../content/${itemId}`} replace />;
}

function ProposalItemRedirect() {
  const { itemId } = useParams<{ itemId: string }>();
  return <Navigate to={`../content/${itemId}`} replace />;
}

function LegacyAuthorRedirect() {
  const { authorId } = useParams<{ authorId: string }>();
  return <Navigate to={`../contributors/${authorId}`} replace />;
}

function GtmCatchAll() {
  const { wsSlug } = useParams<{ wsSlug: string }>();
  return <Navigate to={wsSlug ? `/w/${wsSlug}/sales/contacts` : '/sales/contacts'} replace />;
}

/**
 * The Sales/GTM pane is workspace-scoped, but older URLs (bookmarks,
 * notification links) sometimes embed a projectId before the page segment —
 * e.g. `/w/archie/sales/byczvu0/co/uda5on1` — which the modern router can't
 * match and falls through to the app-level NotFound page.
 *
 * If the first segment looks like a 7-char projectId, strip it and let the
 * inner router resolve the rest. Otherwise fall back to the pane root so the
 * user lands somewhere useful instead of a generic 404.
 */
function LegacyProjectIdStripRedirect() {
  const params = useParams();
  const id = params.maybeProjectId ?? '';
  const rest = params['*'] ?? '';
  const looksLikeProjectId = /^[a-z0-9]{7}$/i.test(id);
  if (!looksLikeProjectId) return <Navigate to=".." replace />;
  return <Navigate to={rest ? `../${rest}` : '..'} replace />;
}

export function GtmRouteChildren() {
  return (
    <>
      {/* Default redirect to All Content */}
      <Route index element={<Navigate to="content" replace />} />

      {/* ================================================================ */}
      {/* CRM ROUTES                                                       */}
      {/* ================================================================ */}
      <Route path="inbox" element={<CrmSuspense><CrmInboxPage /></CrmSuspense>} />
      <Route path="contacts" element={<CrmSuspense><CrmContactsPage /></CrmSuspense>} />
      <Route path="contacts/:entityId" element={<CrmSuspense><CrmContactsPage /></CrmSuspense>} />
      <Route path="companies" element={<CrmSuspense><CrmCompaniesPage /></CrmSuspense>} />
      <Route path="companies/:entityId" element={<CrmSuspense><CrmCompaniesPage /></CrmSuspense>} />
      <Route path="deals" element={<CrmSuspense><CrmDealsPage /></CrmSuspense>} />
      <Route path="deals/:entityId" element={<CrmSuspense><CrmDealsPage /></CrmSuspense>} />
      <Route path="pipeline" element={<CrmSuspense><CrmPipelineLayoutPage /></CrmSuspense>}>
        <Route index element={<Navigate to="forecast" replace />} />
        <Route path="forecast" element={<CrmSuspense><CrmForecastPage /></CrmSuspense>} />
        <Route path="changes" element={<CrmSuspense><CrmPipelineChangesPage /></CrmSuspense>} />
        <Route path="revenue" element={<CrmSuspense><CrmRevenuePage /></CrmSuspense>} />
        <Route path="accounts" element={<CrmSuspense><CrmAccountsPage /></CrmSuspense>} />
        {/* Legacy pipeline sub-routes */}
        <Route path="fc" element={<Navigate to="forecast" replace />} />
        <Route path="pc" element={<Navigate to="changes" replace />} />
        <Route path="rv" element={<Navigate to="revenue" replace />} />
        <Route path="pa" element={<Navigate to="accounts" replace />} />
      </Route>
      <Route path="tasks" element={<CrmSuspense><CrmTasksPage /></CrmSuspense>} />
      <Route path="forms" element={<CrmSuspense><CrmFormsPage /></CrmSuspense>} />
      <Route path="meetings" element={<CrmSuspense><MeetingsListPage /></CrmSuspense>} />
      <Route path="meetings/:conversationId" element={<CrmSuspense><MeetingConversationPage /></CrmSuspense>} />
      <Route path="meeting-detail/:meetingId" element={<CrmSuspense><MeetingDetailPage /></CrmSuspense>} />
      <Route path="my-calendar" element={<CrmSuspense><CalendarPage /></CrmSuspense>} />

      {/* ================================================================ */}
      {/* GTM / CONTENT ROUTES                                             */}
      {/* ================================================================ */}
      <Route path="content" element={<ContentReaderPage />} />
      <Route path="content/:itemId" element={<ContentReaderPage />} />
      <Route path="calendar" element={<ContentCalendarPage />} />

      {/* Contributors */}
      <Route path="contributors" element={<AuthorsPage />} />
      <Route path="contributors/:authorId" element={<AuthorDetailPage />} />

      {/* Distribution & Analytics */}
      <Route path="distribution" element={<DistributionPage />} />
      <Route path="analytics" element={<AnalyticsPage />} />

      {/* Brand Voice */}
      <Route path="brand-voice" element={<BrandVoicePage />} />

      {/* Templates & Type Config */}
      <Route path="pr" element={<Navigate to="../content" replace />} />
      <Route path="pr/:itemId" element={<ProposalItemRedirect />} />
      <Route path="templates" element={<ContentTemplatesPage />} />
      <Route path="type-config" element={<ContentTypeConfigsPage />} />

      {/* Typed content routes — pre-filtered views */}
      <Route path="blog" element={<ContentReaderPage defaultContentType="blog_post" routeSegment="blog" />} />
      <Route path="blog/:itemId" element={<ContentReaderPage defaultContentType="blog_post" routeSegment="blog" />} />
      <Route path="landing-pages" element={<ContentReaderPage defaultContentType="landing_page" routeSegment="landing-pages" />} />
      <Route path="landing-pages/:itemId" element={<ContentReaderPage defaultContentType="landing_page" routeSegment="landing-pages" />} />
      <Route path="website-pages" element={<ContentReaderPage defaultContentType="website_page" routeSegment="website-pages" />} />
      <Route path="website-pages/:itemId" element={<ContentReaderPage defaultContentType="website_page" routeSegment="website-pages" />} />
      <Route path="knowledge-base" element={<ContentReaderPage defaultContentType="kb_article" routeSegment="knowledge-base" />} />
      <Route path="knowledge-base/:itemId" element={<ContentReaderPage defaultContentType="kb_article" routeSegment="knowledge-base" />} />
      <Route path="presentations" element={<ContentReaderPage defaultContentType="presentation" routeSegment="presentations" />} />
      <Route path="presentations/:itemId" element={<ContentReaderPage defaultContentType="presentation" routeSegment="presentations" />} />
      <Route path="case-studies" element={<ContentReaderPage defaultContentType="case_study" routeSegment="case-studies" />} />
      <Route path="case-studies/:itemId" element={<ContentReaderPage defaultContentType="case_study" routeSegment="case-studies" />} />
      <Route path="linkedin" element={<ContentReaderPage defaultContentType="linkedin_post" routeSegment="linkedin" />} />
      <Route path="linkedin/:itemId" element={<ContentReaderPage defaultContentType="linkedin_post" routeSegment="linkedin" />} />
      <Route path="media" element={<MediaBrowserPage />} />
      <Route path="cms-settings" element={<CmsSettingsPage />} />

      {/* Legacy 2-letter code redirects */}
      <Route path="ib" element={<Navigate to="../inbox" replace />} />
      <Route path="ct" element={<Navigate to="../contacts" replace />} />
      <Route path="ct/:entityId" element={<Navigate to="../contacts" replace />} />
      <Route path="cm" element={<Navigate to="../companies" replace />} />
      <Route path="cm/:entityId" element={<Navigate to="../companies" replace />} />
      <Route path="dl" element={<Navigate to="../deals" replace />} />
      <Route path="dl/:entityId" element={<Navigate to="../deals" replace />} />
      <Route path="pl" element={<Navigate to="../pipeline" replace />} />
      <Route path="tk" element={<Navigate to="../tasks" replace />} />
      <Route path="fm" element={<Navigate to="../forms" replace />} />
      <Route path="mc" element={<Navigate to="../meetings" replace />} />
      <Route path="mc/:conversationId" element={<Navigate to="../meetings" replace />} />
      <Route path="md/:meetingId" element={<Navigate to="../meeting-detail" replace />} />
      <Route path="cv" element={<Navigate to="../my-calendar" replace />} />
      <Route path="da" element={<Navigate to="../content" replace />} />
      <Route path="co" element={<Navigate to="../content" replace />} />
      <Route path="co/:itemId" element={<LegacyContentRedirect />} />
      <Route path="ca" element={<Navigate to="../calendar" replace />} />
      <Route path="au" element={<Navigate to="../contributors" replace />} />
      <Route path="au/:authorId" element={<LegacyAuthorRedirect />} />
      <Route path="di" element={<Navigate to="../distribution" replace />} />
      <Route path="an" element={<Navigate to="../analytics" replace />} />
      <Route path="bv" element={<Navigate to="../brand-voice" replace />} />
      <Route path="tp" element={<Navigate to="../templates" replace />} />
      <Route path="ty" element={<Navigate to="../type-config" replace />} />
      <Route path="bl" element={<Navigate to="../blog" replace />} />
      <Route path="lp" element={<Navigate to="../landing-pages" replace />} />
      <Route path="wp" element={<Navigate to="../website-pages" replace />} />
      <Route path="kb" element={<Navigate to="../knowledge-base" replace />} />
      <Route path="ps" element={<Navigate to="../presentations" replace />} />
      <Route path="cs" element={<Navigate to="../case-studies" replace />} />
      <Route path="li" element={<Navigate to="../linkedin" replace />} />
      <Route path="mb" element={<Navigate to="../media" replace />} />
      <Route path="cx" element={<Navigate to="../cms-settings" replace />} />

      {/* Legacy word routes that pointed to old 2-letter codes */}
      <Route path="dashboard" element={<Navigate to="../content" replace />} />
      <Route path="authors" element={<Navigate to="../contributors" replace />} />
      <Route path="authors/:authorId" element={<LegacyAuthorRedirect />} />

      {/* Legacy URLs with an orphan projectId — strip and let the inner
          router resolve the rest. Placed last so explicit routes win. */}
      <Route path=":maybeProjectId" element={<LegacyProjectIdStripRedirect />} />
      <Route path=":maybeProjectId/*" element={<LegacyProjectIdStripRedirect />} />

      {/* No catch-all — prevents infinite redirect loops */}
    </>
  );
}
